import React, { useRef, useCallback, useEffect } from "react";
import {
  ListChildComponentProps,
  ListOnItemsRenderedProps,
  VariableSizeList,
} from "react-window";
import { AutoSizer } from "react-virtualized";
import { cn } from "../../../lib/utils";
import { ArchiveX } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface ExtendedListChildComponentProps extends ListChildComponentProps {
  ref: React.RefObject<HTMLDivElement | null>;
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: (item: any) => void;
  prevItem?: any;
  nextItem?: any;
}

interface VirtualizedListProps<T> {
  items: T[];
  hasMore: boolean;
  renderItem: (props: ExtendedListChildComponentProps) => React.ReactElement;
  renderSkeleton?: (props: ExtendedListChildComponentProps) => React.ReactElement;
  onItemsRendered?: (props: ListOnItemsRenderedProps) => void;
  visibleItems?: number; // Number of items to show in viewport
  className?: string;
  /** Whether the list is in loading state */
  isLoading?: boolean;
  // Selection mode props
  isSelectable?: boolean;
  selectedItemIds?: Set<string>;
  onItemSelect?: (item: any) => void;
  getItemId?: (item: any) => string;
}

export function VirtualizedList<T>({
  items,
  hasMore,
  renderItem,
  renderSkeleton,
  onItemsRendered,
  className,
  isLoading = false,
  isSelectable = false,
  selectedItemIds,
  onItemSelect,
  getItemId,
}: VirtualizedListProps<T>) {
  const { t } = useTranslation();
  const listRef = useRef<VariableSizeList>(null);
  const itemHeights = useRef<Record<number, number>>({});

  // Calculate skeleton count based on container height when loading
  const calculateSkeletonCount = useCallback((containerHeight: number) => {
    const estimatedItemHeight = 100; // Default height estimate
    return Math.ceil(containerHeight / estimatedItemHeight) + 2; // +2 for buffer
  }, []);

  // Use skeleton count when loading with no items, otherwise normal count
  const getItemCount = useCallback((containerHeight?: number) => {
    if (isLoading && items.length === 0 && containerHeight && renderSkeleton) {
      return calculateSkeletonCount(containerHeight);
    }
    return items.length + (hasMore ? 1 : 0);
  }, [isLoading, items.length, hasMore, calculateSkeletonCount, renderSkeleton]);

  const getItemHeight = useCallback((index: number) => {
    return itemHeights.current[index] + 8 || 100;
  }, [itemHeights?.current]);

  const setItemHeight = useCallback((index: number, size: number) => {
    listRef.current?.resetAfterIndex(0);
    itemHeights.current = { ...itemHeights.current, [index]: size };
  }, [itemHeights?.current]);


  const Item = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const itemRef = useRef<HTMLDivElement>(null);

      useEffect(() => {
        if (itemRef.current) {
          setItemHeight(index, itemRef.current.clientHeight);
        }
        // eslint-disable-next-line
      }, [itemRef?.current]);

      // Show skeleton when loading with no items, or when beyond items length and hasMore
      if ((isLoading && items.length === 0) || (index >= items.length && renderSkeleton)) {
        return renderSkeleton!({ index, style, data: null, ref: itemRef });
      }

      const item = items[index];
      const itemId = getItemId ? getItemId(item) : undefined;
      const isSelected = itemId && selectedItemIds ? selectedItemIds.has(itemId) : false;

      return renderItem({ 
        index, 
        style, 
        data: item, 
        ref: itemRef,
        isSelectable,
        isSelected,
        onSelect: onItemSelect,
        prevItem: index > 0 ? items[index - 1] : undefined,
        nextItem: index < items.length - 1 ? items[index + 1] : undefined,
      });
    },
    [items, renderItem, renderSkeleton, isLoading, isSelectable, selectedItemIds, onItemSelect, getItemId]
  );

  return (
    <div
      className={cn(
        "relative w-full h-full overflow-hidden bg-background",
        className
      )}
    >
      {items.length === 0 && !isLoading && (
        <div className="text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
          <ArchiveX className="w-20 h-20" />

          {t("common.noItemsFound")}
        </div>
      )}

      {(items.length > 0 || isLoading) && (
        <AutoSizer>
          {({ height, width }) => {
            const itemCount = getItemCount(height);
            return (
              <VariableSizeList
                ref={listRef}
                height={height}
                width={width}
                itemCount={itemCount}
                itemSize={getItemHeight}
                onItemsRendered={onItemsRendered}
              >
                {Item as any}
              </VariableSizeList>
            );
          }}
        </AutoSizer>
      )}
    </div>
  );
}
